// profileReconcile.js — v227.10.0
// Applies a single profile discrepancy (detected on the Review step) to the saved
// profile, in the exact shape each profile section understands. The AI returns only
// SEMANTIC suggestions; the catalog mapping + write shaping happen here so the
// FAMILY_CONDITIONS / MEDICAL_CONDITIONS catalogs stay single-sourced in the frontend.
//
// Writes go through the store's syncSection(section, fullSectionObject) — the same path
// the profile pages use — so the API persists them correctly and the UI re-reads them.
// v228.15.6: every section now writes directly (no navigation mid-Describe). Medications
// add through the ledger API (medicationsAPI.add, which write-throughs to the profile
// JSONB); unmappable family conditions are stored as custom "other" entries.

import { FAMILY_CONDITIONS, MEDICAL_CONDITIONS } from '../data/medicalLists'
// ★ TEST 1.03 — this file had BOTH shapes three lines apart, exactly like
//   buildProfileSummary: Array.isArray for medicalHistory and `|| []` for familyHistory.
//   One way of saying it.
import { toConditionList } from './profileConditions'

const PROFILE_ROUTES = {
  socialLifestyle: '/profile/social-lifestyle',
  familyHistory: '/profile/family-history',
  medicalHistory: '/profile/medical-history',
  medications: '/profile/medications',
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

// Fuzzy-match a plain condition name to a catalog entry. Exact-ish first, then
// token-overlap. Returns the catalog entry or null.
function matchCatalog(name, catalog) {
  const n = norm(name)
  if (!n) return null
  let exact = catalog.find((c) => norm(c.name) === n || norm(c.id) === n)
  if (exact) return exact
  // contains either direction
  let partial = catalog.find((c) => {
    const cn = norm(c.name)
    return cn.includes(n) || n.includes(cn)
  })
  if (partial) return partial
  // token overlap (>=1 shared significant token)
  const nt = new Set(n.split(' ').filter((w) => w.length > 3))
  let best = null, bestScore = 0
  for (const c of catalog) {
    const ct = norm(c.name).split(' ')
    const score = ct.filter((w) => nt.has(w)).length
    if (score > bestScore) { bestScore = score; best = c }
  }
  return bestScore > 0 ? best : null
}

export function mapFamilyCondition(name) { return matchCatalog(name, FAMILY_CONDITIONS) }
export function mapMedicalCondition(name) { return matchCatalog(name, MEDICAL_CONDITIONS) }

// Apply one discrepancy item. Returns { ok, navigated?, error? }.
//   item  — a discrepancy { section, suggestion, ... }
//   profile — current profile object (from store)
//   actions — { syncSection, updateProfile, navigate }
//     syncSection(section, data) => { success }   (server write)
//     updateProfile({ [section]: data })          (local fallback, mirrors the pages)
//     navigate(path)                              (meds / unmappable fallbacks)
export async function applyDiscrepancy(item, profile, actions = {}) {
  const { syncSection, updateProfile, navigate } = actions
  const sug = item?.suggestion || {}
  const section = item?.section

  // Persist a section the same way the profile pages do: try server, fall back to local.
  const persist = async (sectionKey, data) => {
    const res = await syncSection?.(sectionKey, data)
    if (!res?.success) updateProfile?.({ [sectionKey]: data })
    return { ok: true }
  }

  try {
    if (section === 'socialLifestyle') {
      const field = sug.field // 'smoking' | 'alcohol'
      const value = sug.value // 'Yes' | 'No' | 'Former'
      if (field !== 'smoking' && field !== 'alcohol') return { ok: false, error: 'bad-field' }
      const current = profile?.socialLifestyle || {}
      const freqKey = field + 'Frequency'
      const data = {
        ...current,
        [field]: value,
        // Frequency only meaningful when "Yes"; otherwise clear it (mirrors the page).
        [freqKey]: value === 'Yes' ? (sug.frequency || current[freqKey] || '') : '',
      }
      return await persist('socialLifestyle', data)
    }

    if (section === 'familyHistory') {
      const relative = sug.relative
      if (!['mother', 'father', 'sibling', 'grandparent'].includes(relative)) {
        return { ok: false, error: 'bad-relative' }
      }
      const fh = profile?.familyHistory || {}
      // Rebuild the catalog conditions, preserving any already-saved relative flags.
      const savedById = {}
      toConditionList(fh.conditions).forEach((c) => { if (c && c.id) savedById[c.id] = c })
      const conditions = FAMILY_CONDITIONS.map((c) => ({
        ...c,
        mother: false, father: false, sibling: false, grandparent: false,
        ...(savedById[c.id] || {}),
      }))
      const entry = mapFamilyCondition(sug.condition)
      let otherConditions = Array.isArray(fh.otherConditions) ? [...fh.otherConditions] : []

      if (entry) {
        // Maps to a known catalog condition → set the relative flag in place.
        const target = conditions.find((c) => c.id === entry.id)
        if (target) target[relative] = true
      } else {
        // v228.15.6: unmappable condition → add as a custom "other" entry
        // (same shape the Family History page uses) instead of navigating away.
        const condName = String(sug.condition || '').trim()
        if (!condName) return { ok: false, error: 'empty-condition' }
        const existingIdx = otherConditions.findIndex((o) => norm(o?.name) === norm(condName))
        if (existingIdx >= 0) {
          otherConditions[existingIdx] = { ...otherConditions[existingIdx], [relative]: true }
        } else {
          otherConditions.push({
            id: `custom-${Date.now()}`,
            name: condName,
            mother: false, father: false, sibling: false, grandparent: false,
            [relative]: true,
          })
        }
      }

      const data = {
        conditions,
        otherConditions,
        unknownRelatives: fh.unknownRelatives || { mother: false, father: false, sibling: false },
        completed: true,
      }
      return await persist('familyHistory', data)
    }

    if (section === 'medicalHistory') {
      const mh = profile?.medicalHistory || {}
      const existing = toConditionList(mh.conditions)
      const entry = mapMedicalCondition(sug.condition)
      const name = entry?.name || sug.condition
      const id = entry?.id || `custom-${Date.now()}`
      const dup = existing.some((c) => (c.id && c.id === id) || norm(c.name) === norm(name))
      if (dup) return { ok: true }
      const data = {
        conditions: [...existing, { id, name, year: '', custom: !entry }],
        noConditions: false,
        surgeries: Array.isArray(mh.surgeries) ? mh.surgeries : [],
        noSurgeries: mh.noSurgeries || false,
        completed: true,
      }
      return await persist('medicalHistory', data)
    }

    if (section === 'medications') {
      // v228.15.6: add the medication directly (no navigation mid-Describe).
      // The AI now supplies a best-effort name; fall back to label/said.
      const name = String(sug.name || item?.label || item?.said || '').trim()
      if (!name) {
        // Only navigate as an absolute last resort (model returned nothing usable).
        navigate?.(PROFILE_ROUTES.medications)
        return { ok: true, navigated: true }
      }
      const meds = profile?.medications || {}
      const list = Array.isArray(meds.list) ? meds.list : []
      const dup = list.some((m) => norm(m?.name) === norm(name))

      let created = null
      try {
        const { medicationsAPI } = await import('../services/api')
        created = await medicationsAPI.add({ name, source: 'self_reported', medType: 'chronic' })
      } catch (e) {
        // Ledger write failed → we still persist to the profile JSONB below.
      }

      if (!dup) {
        const nextList = [...list, {
          id: created?.id || `med-${Date.now()}`,
          name,
          dosage: '', frequency: '', purpose: '',
          status: 'active',
          needsReview: true, // patient can add dose/frequency later
        }]
        const data = { ...meds, hasMedications: true, noMedications: false, list: nextList, completed: true }
        if (created) {
          // Ledger add already write-through'd to profiles.medications on the
          // server; just mirror into the local store for this session.
          updateProfile?.({ medications: data })
        } else {
          // Ledger unavailable → persist the JSONB ourselves.
          await persist('medications', data)
        }
      }
      return { ok: true }
    }

    return { ok: false, error: 'unknown-section' }
  } catch (err) {
    console.error('[profileReconcile] apply failed:', err)
    return { ok: false, error: err?.message || 'apply-failed' }
  }
}

export { PROFILE_ROUTES }
